/*
 * Copyright 2026 Sony Corporation
 */

/**
 * Connecting to Sketchfab, as the user rather than as the application.
 *
 * Their Download API requires the *end user's* own account (devplan §5.8), and
 * registering an OAuth client is not self-service — it is a support request.
 * So the connection is the user's own **API token**, pasted once: it is the
 * same account the OAuth flow would have authenticated, without the ceremony.
 *
 * The token never leaves the browser. It is not in `config.json`, not in the
 * bundle, and never sent to our own API — every call it authorises goes
 * straight to Sketchfab. It is held in `sessionStorage` so it dies with the
 * tab; a credential for somebody else's service is not something to leave on a
 * shared machine.
 */

const KEY = 'device-verse:sketchfab';

export interface SketchfabSession {
  /** Sent as `Authorization: Token …`, which is their form for an account token. */
  token: string;
  username: string;
}

const listeners = new Set<() => void>();

let cached: SketchfabSession | null = null;
let cachedRaw: string | null = null;

/**
 * The stored session, or null.
 *
 * Memoised against the raw string because `useSyncExternalStore` compares
 * snapshots by identity and would otherwise re-render forever on a fresh parse.
 */
export const readSession = (): SketchfabSession | null => {
  const raw = window.sessionStorage.getItem(KEY);
  if (raw !== cachedRaw) {
    cachedRaw = raw;
    cached = null;

    if (raw) {
      try {
        const parsed = JSON.parse(raw) as SketchfabSession;
        cached = typeof parsed.token === 'string' && typeof parsed.username === 'string' ? parsed : null;
      } catch {
        cached = null;
      }
    }
  }
  return cached;
};

export const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  // Another tab connecting is a change this one should show.
  window.addEventListener('storage', listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', listener);
  };
};

const write = (session: SketchfabSession | null): void => {
  if (session) {
    window.sessionStorage.setItem(KEY, JSON.stringify(session));
  } else {
    window.sessionStorage.removeItem(KEY);
  }
  for (const listener of listeners) {
    listener();
  }
};

export const disconnect = (): void => write(null);

/** Their header form for an account token, as opposed to `Bearer` for an OAuth one. */
export const authorization = (session: SketchfabSession): string => `Token ${session.token}`;

/**
 * Stores the token, having checked it is one.
 *
 * `/v3/me` is the check: a token that cannot name its own account is not worth
 * keeping, and finding that out now beats finding it out halfway through an
 * import. The username it returns is also what the menu shows.
 */
export const connect = async (token: string): Promise<{ error?: string }> => {
  const trimmed = token.trim();
  if (!trimmed) {
    return { error: 'Paste your Sketchfab API token.' };
  }

  const response = await fetch('https://api.sketchfab.com/v3/me', {
    headers: { Authorization: `Token ${trimmed}` },
  }).catch(() => undefined);

  if (!response) {
    return { error: 'Sketchfab could not be reached.' };
  }
  if (response.status === 401 || response.status === 403) {
    return { error: 'Sketchfab did not accept that token.' };
  }
  if (!response.ok) {
    return { error: `Sketchfab answered ${response.status}.` };
  }

  const body = (await response.json().catch(() => undefined)) as { username?: string } | undefined;
  if (!body?.username) {
    return { error: 'Sketchfab returned an account this app does not understand.' };
  }

  write({ token: trimmed, username: body.username });
  return {};
};
